import React, { LegacyRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { TextLink } from '@spotify-internal/encore-web';
import { PageLoadingIndicator } from '@spotify-internal/anchor-ds';
import * as Sentry from '@sentry/browser';

import { AnchorAPI } from 'client/modules/AnchorAPI';
import CreateAccountContainer from 'client/components/CreateAccountContainer';
import { LoginContainer } from 'client/components/LoginContainer';
import RequestResetPasswordConfirmationContainer from 'client/components/RequestResetPasswordConfirmationContainer';
import RequestResetPasswordContainer from 'client/components/RequestResetPasswordContainer';
import events from 'client/components/SignupPageContainer/events';
import { useT } from 'client/i18n/hooks/useT';
import { paths } from 'client/shared/Paths';
import { INTEGRATION_CONNECTION_MENU_MODE } from 'client/screens/IntegrationConnectionScreen/constants';
import { LinkText } from 'shared/Link';
import { PODCAST_COLORS } from 'client/utils';
import { SwitchModal } from 'client/screens/IntegrationConnectionScreen/components';
import { useUpdateUserIntegrationsData } from 'client/hooks/user/useUpdateUserIntegrationsData';
import {
  trackRiversideLoginClicked,
  trackRiversideSignupClicked,
  trackRiversideSwitchClicked,
} from '../../events';
import {
  LoginSignupContainer,
  LoginSignupHeading,
  LoginSignUpOption,
  LoginSignUpTerms,
} from './styles';

type LoginSignupProps = {
  currentAuthFormView: INTEGRATION_CONNECTION_MENU_MODE;
  reference: LegacyRef<HTMLDivElement> | undefined;
  returnToRedirectPath: string;
  setCurrentAuthFormView: (value: INTEGRATION_CONNECTION_MENU_MODE) => void;
  showHeroLoginSignUpForm: boolean;
  partnerName: string;
};

export function LoginSignUp({
  currentAuthFormView,
  reference,
  returnToRedirectPath,
  setCurrentAuthFormView,
  showHeroLoginSignUpForm,
  partnerName,
}: LoginSignupProps) {
  const [showSwitchModal, setShowSwitchModal] = useState<boolean>(false);
  const history = useHistory();
  const t = useT();
  const { updateIntegrationsData } = useUpdateUserIntegrationsData();

  const isSignup = currentAuthFormView === 'create';

  const heading = isSignup
    ? t(
        'IntegrationConnectionScreen.LoginSignUp.Subheading.SignUp',
        'Create an Anchor account',
        'The heading when a user hits the integration connection screen to create an account'
      )
    : t(
        'IntegrationConnectionScreen.LoginSignUp.Subheading.Login',
        'Log in to an Anchor account',
        'The heading when a user hits the integration connection screen to login to an existing account'
      );

  const onSignupClick = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault();
    setCurrentAuthFormView('create');
    trackRiversideSignupClicked();
  };

  const onLoginClick = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault();
    setCurrentAuthFormView('login');
    trackRiversideLoginClicked();
  };

  const redirectLink = (
    <TextLink
      component="button"
      key={
        isSignup
          ? 'integrationConnectionScreenLoginLinkKey'
          : 'integrationConnectionScreenSignUpLinkKey'
      }
      onClick={isSignup ? onLoginClick : onSignupClick}
    >
      {isSignup
        ? t('SignupPage.LoginLinkText', 'Log in', 'Log in link text')
        : t('Link.Signup', 'Sign up', 'Sign up link text')}
    </TextLink>
  );

  const redirectOption = isSignup
    ? t(
        'SignupForm.LoginInsteadMessage',
        'Already have an account? {loginLink} instead.',
        'Sign up form login instead message',
        { loginLink: redirectLink }
      )
    : t(
        'Login.SignupMessage',
        "Don't have an account? {signupLink} instead.",
        'Login page sign up message',
        { signupLink: redirectLink }
      );

  const onSwitchClicked = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault();
    trackRiversideSwitchClicked();
    setShowSwitchModal(true);
  };

  const importOption = t(
    'IntegrationConnectionScreen.LoginSignUp.Import',
    'You can also { importLink } an existing podcast to Anchor.',
    'The import message to give the option to import their whole podcast via an RSS feed. The full text in English with variables is: You can also switch an existing podcast to Anchor.',
    {
      importLink: (
        <TextLink
          component="button"
          key="integrationConnectionScreenImportLink"
          onClick={onSwitchClicked}
          style={{ padding: 0 }}
        >
          {t(
            'IntegrationConnectionScreen.LoginSignUp.ImportLink',
            'switch',
            'The import link to navigate a user to switch their whole podcast via an RSS feed'
          )}
        </TextLink>
      ),
    }
  );

  const signupTerms = t(
    'SignupForm.TermsOfServiceAgreementMessage',
    'By continuing, you agree to our {termsOfServiceLink} and {privacyPolicyLink}.',
    'Sign up terms of service agreement message',
    {
      termsOfServiceLink: (
        <LinkText
          key="integrationConnectionScreenSignUpTOSLink"
          target="_blank"
          onClick={() => events.signUpLegalLinkClicked({ link: 'tos' })}
          isInline={true}
          to={paths.legal.tos}
          color="gray"
        >
          {t(
            'Link.TermsOfService',
            'Terms of Service',
            'Terms of Service link text'
          )}
        </LinkText>
      ),
      privacyPolicyLink: (
        <LinkText
          key="integrationConnectionScreenSignUpPrivacyLink"
          target="_blank"
          onClick={() => events.signUpLegalLinkClicked({ link: 'privacy' })}
          isInline={true}
          color="gray"
          to={paths.legal.privacy}
        >
          {t(
            'Link.PrivacyPolicy',
            'Privacy Policy',
            'Privacy Policy link text'
          )}
        </LinkText>
      ),
    }
  );

  const onPasswordResetSubmit = ({ email }: { email: string }) => {
    return AnchorAPI.requestPasswordReset({ emailAddress: email })
      .then(response => {
        const { status } = response;
        if (status === 400) {
          throw new Error('There was a problem with this email.');
        }

        // assume 200 ok
        setCurrentAuthFormView('requestResetPasswordConfirmation');
      })
      .catch(() => {
        throw new Error(
          `There was a problem requesting a password reset. Please reach out to support for more help at ${paths.getHelpPage(
            'home'
          )}.`
        );
      });
  };

  const onSubmitCallback = (userId: number, newUser?: boolean) => {
    updateIntegrationsData({
      userId,
      integrationName: partnerName,
      ...(newUser && { userOrigin: partnerName }),
    });
    Sentry.captureMessage(
      `updateIntegrationsData called for user ${userId} from integration landing page who is ${
        newUser ? 'signing up' : 'logging in'
      }`
    );
    history.replace(returnToRedirectPath);
  };

  const renderForm = () => {
    switch (currentAuthFormView) {
      case 'login':
        return (
          <LoginContainer
            integrationLanderAuthenticatorRedirectPath={returnToRedirectPath}
            loginCallback={onSubmitCallback}
            setIntegrationConnectionScreenStatus={setCurrentAuthFormView}
            shouldRedirectToDashboard={false}
            showSignUp={false}
          />
        );
      case 'requestResetPassword':
        return (
          <RequestResetPasswordContainer
            onVideoLanderSubmit={onPasswordResetSubmit}
            setIntegrationConnectionScreenStatus={setCurrentAuthFormView}
          />
        );
      case 'requestResetPasswordConfirmation':
        return (
          <RequestResetPasswordConfirmationContainer
            setIntegrationConnectionScreenStatus={setCurrentAuthFormView}
          />
        );
      case 'create':
        return (
          <CreateAccountContainer
            enableAutoFocus={false}
            isIntegrationReferral={true}
            isV3Captcha={false}
            submitCallback={(userId: number) => onSubmitCallback(userId, true)}
          />
        );
      default:
        return <PageLoadingIndicator color={PODCAST_COLORS.WHITE.color} />;
    }
  };

  return (
    <LoginSignupContainer
      ref={reference}
      showHeroLoginSignUpForm={showHeroLoginSignUpForm}
    >
      <LoginSignupHeading>{heading}</LoginSignupHeading>
      <LoginSignUpOption>{redirectOption}</LoginSignUpOption>
      {renderForm()}
      <LoginSignUpOption isLogin={!isSignup}>{importOption}</LoginSignUpOption>
      {isSignup && <LoginSignUpTerms>{signupTerms}</LoginSignUpTerms>}
      {showSwitchModal && (
        <SwitchModal
          partnerName={partnerName}
          dismissSwitchModal={() => setShowSwitchModal(false)}
        />
      )}
    </LoginSignupContainer>
  );
}
